import React, { useState } from 'react';
import { ChevronDown, ChevronRight, Star, Check, X } from 'lucide-react';

interface TakeawayItem {
    title: string;
    highlights: string[];
    pros?: string[];
    cons?: string[];
}

interface KeyTakeawaysProps {
    items: TakeawayItem[];
    title?: string;
}

const KeyTakeaways: React.FC<KeyTakeawaysProps> = ({
    items,
    title = "Pontos-Chave"
}) => {
    const [expandedIndex, setExpandedIndex] = useState<number | null>(0);

    if (!items || items.length === 0) return null;

    return (
        <div className="my-8">
            <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Star className="w-6 h-6 text-yellow-500" />
                {title}
            </h3>
            <div className="space-y-3">
                {items.map((item, index) => (
                    <div
                        key={index}
                        className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm hover:shadow-md transition-shadow"
                    >
                        <button
                            onClick={() => setExpandedIndex(expandedIndex === index ? null : index)}
                            className="w-full px-5 py-4 flex items-center justify-between text-left hover:bg-gray-50 transition-colors"
                        >
                            <span className="font-semibold text-gray-900">{item.title}</span>
                            {expandedIndex === index ? (
                                <ChevronDown className="w-5 h-5 text-gray-500" />
                            ) : (
                                <ChevronRight className="w-5 h-5 text-gray-500" />
                            )}
                        </button>

                        {expandedIndex === index && (
                            <div className="px-5 pb-5 border-t border-gray-100">
                                {/* Highlights */}
                                <div className="mt-4 space-y-2">
                                    {item.highlights.map((highlight, hIndex) => (
                                        <p key={hIndex} className="text-gray-600 text-sm leading-relaxed">
                                            • {highlight}
                                        </p>
                                    ))}
                                </div>

                                {/* Pros and Cons */}
                                {(item.pros || item.cons) && (
                                    <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                                        {item.pros && item.pros.length > 0 && (
                                            <div className="bg-green-50 rounded-lg p-4">
                                                <h5 className="font-semibold text-green-800 mb-2 flex items-center gap-2">
                                                    <Check className="w-4 h-4" /> Vantagens
                                                </h5>
                                                <ul className="space-y-1">
                                                    {item.pros.map((pro, pIndex) => (
                                                        <li key={pIndex} className="text-sm text-green-700">
                                                            + {pro}
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}
                                        {item.cons && item.cons.length > 0 && (
                                            <div className="bg-red-50 rounded-lg p-4">
                                                <h5 className="font-semibold text-red-800 mb-2 flex items-center gap-2">
                                                    <X className="w-4 h-4" /> Desvantagens
                                                </h5>
                                                <ul className="space-y-1">
                                                    {item.cons.map((con, cIndex) => (
                                                        <li key={cIndex} className="text-sm text-red-700">
                                                            - {con}
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
};

export default KeyTakeaways;
